/**
 * Dialog REVELAR SENHA: dispara o POST reveal (mutation — o segredo vive só
 * no estado deste componente e morre no unmount/fechamento), exibe em mono
 * com blur inicial ('Clique para exibir'), Copiar e auto-fechamento em 30s
 * com contador discreto.
 */
import { useEffect, useRef, useState } from 'react';
import { Copy } from 'lucide-react';
import type { VaultEntry } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Dialog, Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { copyText } from './vault-clipboard';
import { useRevealVaultEntry } from './vault-mutations';
import { useVaultToast } from './vault-toast';
import { vaultErrorMessage } from './vault-format';

const AUTO_CLOSE_SECONDS = 30;

export function RevealDialog({ entry, onClose }: { entry: VaultEntry; onClose: () => void }) {
  const toast = useVaultToast();
  const reveal = useRevealVaultEntry();
  const [visible, setVisible] = useState(false);
  const [remaining, setRemaining] = useState(AUTO_CLOSE_SECONDS);

  const secret = reveal.data ?? null;

  // Um único reveal por abertura do dialog.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    reveal.mutate(entry.id);
  }, [entry.id, reveal]);

  // Contagem regressiva a partir do momento em que o segredo chega.
  useEffect(() => {
    if (secret === null) return;
    setRemaining(AUTO_CLOSE_SECONDS);
    const t = setInterval(() => setRemaining((r) => r - 1), 1000);
    return () => clearInterval(t);
  }, [secret]);

  useEffect(() => {
    if (remaining <= 0) onClose();
  }, [remaining, onClose]);

  const copy = async () => {
    if (secret === null) return;
    const ok = await copyText(secret);
    toast(ok ? 'Senha copiada' : 'Não foi possível copiar');
  };

  return (
    <Dialog open onClose={onClose} title="Revelar senha">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Senha de <span className="font-medium text-foreground">{entry.name}</span>. A revelação
          fica registrada no histórico do cofre.
        </p>

        {reveal.isPending ? (
          <div className="flex h-16 items-center justify-center">
            <Spinner />
          </div>
        ) : reveal.isError ? (
          <p className="text-sm text-destructive" role="alert">
            {vaultErrorMessage(reveal.error)}
          </p>
        ) : secret !== null ? (
          <div className="relative rounded-md border bg-muted/50 px-3 py-3">
            <code
              aria-hidden={!visible}
              className={cn(
                'block min-h-5 font-mono text-sm break-all',
                !visible && 'blur-sm select-none'
              )}
            >
              {secret}
            </code>
            {!visible ? (
              <button
                type="button"
                onClick={() => setVisible(true)}
                className="absolute inset-0 flex items-center justify-center rounded-md text-[13px] font-medium text-foreground/80 transition-colors hover:bg-muted/40"
              >
                Clique para exibir
              </button>
            ) : null}
          </div>
        ) : null}

        <div className="flex items-center justify-between gap-2 border-t pt-3">
          <span className="text-xs text-muted-foreground tabular-nums" aria-live="off">
            {secret !== null ? `Fecha em ${remaining}s` : ''}
          </span>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Fechar
            </Button>
            <Button type="button" size="sm" disabled={secret === null} onClick={() => void copy()}>
              <Copy className="size-4" />
              Copiar
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
